import { useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Play, Clock, ChevronDown, ChevronUp, Loader2, AlertCircle } from 'lucide-react'
import { getPlate, getPlateRuns } from '../api/brainer'
import { PlateLayout } from '../components/PlateLayout'
import { Button } from '../components/ui/button'
import type { BPlateRun, BPlateInputParam } from '../types'

export function BrainerPlateRunner() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const { data: plate, isLoading } = useQuery({
    queryKey: ['b-plate', id],
    queryFn: () => getPlate(id!).then(r => r.data),
    enabled: !!id,
  })

  const { data: runs = [], refetch: refetchRuns } = useQuery({
    queryKey: ['b-plate-runs', id],
    queryFn: () => getPlateRuns(id!).then(r => r.data),
    enabled: !!id,
  })

  const [runInputs,   setRunInputs]   = useState<Record<string, string> | null>(null)
  const [showHistory, setShowHistory] = useState(false)

  // Compute effective param values: fixedValue > URL param
  const resolveInputs = (params: BPlateInputParam[]): Record<string, string> => {
    const result: Record<string, string> = {}
    for (const p of params) {
      const val = p.fixedValue ?? searchParams.get(p.name) ?? ''
      if (val) result[p.name] = val
    }
    return result
  }

  // Auto-run once plate loads if all required params are satisfied
  useEffect(() => {
    if (!plate || runInputs !== null) return
    const inputs = resolveInputs(plate.inputParams)
    const missingRequired = plate.inputParams.filter(p => p.required && !inputs[p.name]?.trim())
    if (missingRequired.length === 0) {
      setRunInputs(inputs)
    }
  }, [plate]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) return <div className="p-8 text-center text-sm text-ink-dim">Loading</div>
  if (!plate)    return <div className="p-8 text-center text-sm text-ink-dim">Plate not found</div>

  if (plate.status !== 'deployed') {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-ink-dim mb-3">This plate is not deployed yet.</p>
        <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate(`/brainer/plates/${id}`)} className="mx-auto">
          Back to builder
        </Button>
      </div>
    )
  }

  const resolvedInputs = resolveInputs(plate.inputParams)
  const missingRequired = plate.inputParams
    .filter(p => p.required && !resolvedInputs[p.name]?.trim())
    .map(p => p.name)

  const handleRun = () => {
    setRunInputs({ ...resolvedInputs })
    refetchRuns()
  }

  const handleParamUpdate = (updates: Record<string, string>) => {
    console.log('[handleParamUpdate]', updates)
    setRunInputs(prev => {
      if (!prev) return prev
      const merged = { ...prev, ...updates }
      return JSON.stringify(merged) === JSON.stringify(prev) ? prev : merged
    })
  }

  return (
    <div>
      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/brainer/plates/${id}`)} className="text-ink-dim hover:text-ink transition-colors">
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-ink">{plate.name}</h1>
            {plate.subjectType && (
              <p className="text-xs text-ink-dim mt-0.5">Subject: <span className="font-mono">{plate.subjectType}</span></p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" icon={Clock} onClick={() => setShowHistory(h => !h)}>
            History {showHistory ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          </Button>
          <Button icon={Play} onClick={handleRun} disabled={missingRequired.length > 0}>Run</Button>
        </div>
      </div>

      {/* ── Missing required params warning ──────────────────────── */}
      {missingRequired.length > 0 && (
        <div className="mb-4 border border-amber-100 rounded-lg p-3 bg-amber-50 flex items-start gap-2">
          <AlertCircle size={14} className="text-amber-500 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-700">
            Cannot run — missing required params: <span className="font-mono font-medium">{missingRequired.join(', ')}</span>
          </p>
        </div>
      )}

      {/* ── Run history ──────────────────────────────────────────── */}
      {showHistory && (
        <div className="mb-4 table-wrap">
          <div className="bg-paper-sunken px-3 py-1.5 border-b border-paper-border text-xs font-medium text-ink-dim">
            Recent runs
          </div>
          {(runs as BPlateRun[]).length === 0 ? (
            <p className="px-3 py-3 text-xs text-ink-dim">No runs yet</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Status</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {(runs as BPlateRun[]).map(run => {
                  const dur = run.completedAt
                    ? Math.round((new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime()) / 100) / 10
                    : null
                  return (
                    <tr key={run.id}>
                      <td className="text-ink-dim">{new Date(run.startedAt).toLocaleString()}</td>
                      <td>
                        <span className={`px-1.5 py-0.5 rounded text-theme-xs ${
                          run.status === 'completed' ? 'bg-emerald-50 text-emerald-600'
                          : run.status === 'failed'  ? 'bg-red-50 text-red-500'
                          : 'bg-paper-sunken text-ink-dim'
                        }`}>{run.status}</span>
                      </td>
                      <td className="text-ink-dim">{dur != null ? `${dur}s` : '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Plate layout ─────────────────────────────────────────── */}
      {runInputs ? (
        <div className={plate.width ? `${plate.width} mx-auto` : undefined}>
          <PlateLayout plate={plate} runInputs={runInputs} onParamUpdate={handleParamUpdate} />
        </div>
      ) : missingRequired.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={20} className="animate-spin text-ink-dim" />
        </div>
      ) : (
        <div className="rounded border border-dashed border-paper-border p-12 text-center text-sm text-ink-dim">
          Provide the required parameters to run this plate
        </div>
      )}
    </div>
  )
}

